import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import type { AppMessages } from "@/i18n/ja";
import {
  createStickyTooltipController,
  type StickyTooltipController,
  type StickyTooltipSource,
} from "@/shared/ui/stickyTooltipController";
import {
  FAST_OUTSIDE_HOV_MAX_DURATION_MS,
  QUICK_OUTSIDE_HOV_MAX_DURATION_MS,
  SWIFT_OUTSIDE_HOV_MAX_DURATION_MS,
  GOOGLE_HOV_THRESHOLD,
  HEAVY_POINTER_HOLD_SECONDS,
  GLOBAL_HOV_GAIN_UPGRADE_MILESTONE_ACHIEVEMENT_LEVEL,
  GLOBAL_HOV_GAIN_UPGRADE_TENTH_MILESTONE_ACHIEVEMENT_LEVEL,
  H9G_UPGRADE_MILESTONE_ACHIEVEMENT_LEVEL,
  HOV_GAIN_FAMILY_UPGRADE_PURCHASE_THRESHOLD,
  HOV_GAIN_FAMILY_UPGRADE_MARATHON_PURCHASE_THRESHOLD,
  MASS_HOV_GAIN_FAMILY_UPGRADE_PURCHASE_THRESHOLD,
  ONE_COST_BATCH_UPGRADE_HOV_GAIN_LEVEL,
  MUSIC_POINTER_ASSIGNED_MP_THRESHOLD,
  MOVIE_POINTER_ASSIGNED_MP_THRESHOLD,
  MASCHINEN_POINTER_ASSIGNED_MP_THRESHOLD,
  TIM_HOV_ACTIVATION_UNLOCK_HOV_REFINED_FROM_TIM_THRESHOLD,
  TIME_HAS_COME_TOTAL_TIM_SPENT_THRESHOLD,
  BURN_THE_CORE_TOTAL_HOV_CORE_SPENT_THRESHOLD,
  AMPLIFIER_UPGRADE_PURCHASE_THRESHOLD,
  VIGINTILLION_HOV_THRESHOLD,
  HOV_CELL_RAPID_CLICK_TARGET,
  HOV_O_LABEL_HOVER_SECONDS,
  BILLION_HOV_THRESHOLD,
  POP_GRID_INCREASE_TARGET_SECONDS,
  BOMB_GRID_INCREASE_TARGET_SECONDS,
  NOT_FULLY_FREE_HG_COST_DIVISOR_THRESHOLD,
  MASSIVE_MERGE_HOV_MHOV_MULTIPLIER_THRESHOLD,
  RANK_GAP_H9G_TO_H1G_RATIO_THRESHOLD,
  NHOV_KNOWS_NHOV_HG_EFFECTS_TO_HNG_THRESHOLD,
  HOV_1_GAIN_TRIES_TOO_LOG10_THRESHOLD,
  HOV_1_GAIN_GETS_SERIOUS_LOG10_THRESHOLD,
  EXPLORATION_BIOME_LEVEL_ACHIEVEMENT_THRESHOLD,
} from "./achievementDefinitions";
import {
  HOV_GRID_INCREASE_1_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
  HOV_GRID_INCREASE_2_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD,
} from "@/features/hoverIdle/unlocks/hoverIdleEffectUnlocks";
import { huge } from "@/shared/numbers";
import { formatForContext } from "@/shared/format/numberFormat";
import { getHovSkillDefinition } from "@/features/hoverIdle/hov/hovSkillDefinitions";
import { formatHovSkillCoordinate } from "@/features/hoverIdle/hov/hovSkillFormat";
import {
  ACHIEVEMENT_SECRET_TOKEN_GRANT,
  FOURTH_HOV_GAIN_MP_BONUS,
  GOOGLE_HG_POWER_EXPONENT_BONUS,
  GLOBAL_HOV_GAIN_UPGRADE_TENTH_MILESTONE_HG_POWER_EXPONENT_BONUS,
  MASSIVE_MERGE_HOV_HG_POWER_EXPONENT_BONUS,
  RANK_GAP_HG_POWER_EXPONENT_BONUS,
  TENTH_OUTSIDE_HOV_HG_POWER_EXPONENT_BONUS,
  HOV_1_GAIN_TRIES_TOO_H1G_EXPONENT_BONUS,
  HOV_1_GAIN_GETS_SERIOUS_H1G_EXPONENT_BONUS,
  MASCHINEN_POINTER_MP_BONUS,
  MASS_HOV_GAIN_FAMILY_UPGRADE_MP_BONUS,
  MHOV_MP_EXECUTION_TEN_MP_BONUS,
  MOVIE_POINTER_MP_BONUS,
  MUSIC_POINTER_MP_BONUS,
  NINTH_HOV_GAIN_MP_BONUS,
  NEW_NEW_HOV_HG_MULTIPLIER,
  SECOND_MHOV_HG_MULTIPLIER,
  TIM_HOV_ACTIVATION_EFFICIENCY_AFTER_TIME_HAS_COME,
  CHEAP_FOURTH_HOV_GAIN_HG_HNG_COST_DIVISOR,
  CHEAP_GLOBAL_HOV_GAIN_MILESTONE_HOV_GAIN_FAMILY_COST_DIVISOR,
  NOT_FULLY_FREE_HG_HNG_COST_DIVISOR,
  EXPLORATION_BIOME_LEVEL_MP_BONUS,
} from "./achievementEffects";
import { EXPLORATION_TIM_ACCELERATION_UNLOCK_CUMULATIVE_MS } from "@/features/hoverIdle/exploration/explorationDefinitions";
import { HOV_CELL_RAPID_CLICK_WINDOW_MS } from "@/features/hoverIdle/core/hoverIdleModel";
import type {
  AchievementCategory,
  AchievementDefinition,
  AchievementId,
  NormalAchievementId,
  SecretAchievementId,
  AchievementsSnapshot,
} from "./types";
import { UPGRADE_MILESTONE_INTERVAL } from "@/features/hoverIdle/core/upgradeMilestones";

const HEAVY_POINTER_HOLD_MINUTES = Math.round(HEAVY_POINTER_HOLD_SECONDS / 60);
const FAST_OUTSIDE_HOV_MAX_DURATION_HOURS = Math.round(
  FAST_OUTSIDE_HOV_MAX_DURATION_MS / (60 * 60 * 1000),
);
const QUICK_OUTSIDE_HOV_MAX_DURATION_MINUTES = Math.round(
  QUICK_OUTSIDE_HOV_MAX_DURATION_MS / (60 * 1000),
);
const SWIFT_OUTSIDE_HOV_MAX_DURATION_MINUTES = Math.round(
  SWIFT_OUTSIDE_HOV_MAX_DURATION_MS / (60 * 1000),
);
const HOV_CELL_RAPID_CLICK_WINDOW_SECONDS = Math.round(
  HOV_CELL_RAPID_CLICK_WINDOW_MS / 1000,
);
const POP_GRID_INCREASE_HG_PER_SECOND_THRESHOLD =
  HOV_GRID_INCREASE_1_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD /
  POP_GRID_INCREASE_TARGET_SECONDS;
const BOMB_GRID_INCREASE_HG_PER_SECOND_THRESHOLD =
  HOV_GRID_INCREASE_2_UNLOCK_CURRENT_LOOP_HOV_THRESHOLD /
  BOMB_GRID_INCREASE_TARGET_SECONDS;
const ACHIEVEMENT_FLOATING_TOOLTIP_OPTIONS = {
  preferredPlacement: "bottom",
  offsetPx: 6,
} as const;
const RECENT_UNLOCK_CLASS = "hover-idle-achievement-icon--recently-unlocked";

function resolveNormalCondition(
  id: NormalAchievementId,
  conditions: AppMessages["achievements"]["conditions"],
): string {
  switch (id) {
    case "hov-gain-family-upgrade":
      return conditions["hov-gain-family-upgrade"](
        HOV_GAIN_FAMILY_UPGRADE_PURCHASE_THRESHOLD,
      );
    case "hov-gain-family-upgrade-marathon":
      return conditions["hov-gain-family-upgrade-marathon"](
        HOV_GAIN_FAMILY_UPGRADE_MARATHON_PURCHASE_THRESHOLD,
      );
    case "tim-hov-activation":
      return conditions["tim-hov-activation"](
        TIM_HOV_ACTIVATION_UNLOCK_HOV_REFINED_FROM_TIM_THRESHOLD,
      );
    case "time-has-come":
      return conditions["time-has-come"](
        TIME_HAS_COME_TOTAL_TIM_SPENT_THRESHOLD,
      );
    case "billion":
      return conditions["billion"](
        formatForContext(huge(BILLION_HOV_THRESHOLD), "main"),
      );
    case "mass-hov-gain-family-upgrade":
      return conditions["mass-hov-gain-family-upgrade"](
        MASS_HOV_GAIN_FAMILY_UPGRADE_PURCHASE_THRESHOLD,
      );
    case "music-pointer":
      return conditions["music-pointer"](MUSIC_POINTER_ASSIGNED_MP_THRESHOLD);
    case "movie-pointer":
      return conditions["movie-pointer"](MOVIE_POINTER_ASSIGNED_MP_THRESHOLD);
    case "maschinen-pointer":
      return conditions["maschinen-pointer"](
        MASCHINEN_POINTER_ASSIGNED_MP_THRESHOLD,
      );
    case "vigintillion":
      return conditions["vigintillion"](
        formatForContext(huge(VIGINTILLION_HOV_THRESHOLD), "main"),
      );
    case "google":
      return conditions["google"](
        formatForContext(huge(GOOGLE_HOV_THRESHOLD), "main"),
      );
    case "pop":
      return conditions["pop"](
        formatForContext(
          huge(POP_GRID_INCREASE_HG_PER_SECOND_THRESHOLD),
          "main",
        ),
      );
    case "bomb":
      return conditions["bomb"](
        formatForContext(
          huge(BOMB_GRID_INCREASE_HG_PER_SECOND_THRESHOLD),
          "main",
        ),
      );
    case "fast-outside-hov":
      return conditions["fast-outside-hov"](
        FAST_OUTSIDE_HOV_MAX_DURATION_HOURS,
      );
    case "quick-outside-hov":
      return conditions["quick-outside-hov"](
        QUICK_OUTSIDE_HOV_MAX_DURATION_MINUTES,
      );
    case "swift-outside-hov":
      return conditions["swift-outside-hov"](
        SWIFT_OUTSIDE_HOV_MAX_DURATION_MINUTES,
      );
    case "global-hov-gain-upgrade-milestone":
      return conditions["global-hov-gain-upgrade-milestone"](
        GLOBAL_HOV_GAIN_UPGRADE_MILESTONE_ACHIEVEMENT_LEVEL,
      );
    case "global-hov-gain-upgrade-tenth-milestone":
      return conditions["global-hov-gain-upgrade-tenth-milestone"](
        GLOBAL_HOV_GAIN_UPGRADE_TENTH_MILESTONE_ACHIEVEMENT_LEVEL,
      );
    case "not-fully-free":
      return conditions["not-fully-free"](
        formatForContext(
          huge(NOT_FULLY_FREE_HG_COST_DIVISOR_THRESHOLD),
          "main",
        ),
      );
    case "nhov-knows-nhov":
      return conditions["nhov-knows-nhov"](
        formatForContext(
          huge(NHOV_KNOWS_NHOV_HG_EFFECTS_TO_HNG_THRESHOLD),
          "main",
        ),
      );
    case "massive-merge-hov":
      return conditions["massive-merge-hov"](
        `x${formatForContext(
          huge(MASSIVE_MERGE_HOV_MHOV_MULTIPLIER_THRESHOLD),
          "main",
        )}`,
      );
    case "rank-gap":
      return conditions["rank-gap"](
        `x${formatForContext(
          huge(RANK_GAP_H9G_TO_H1G_RATIO_THRESHOLD),
          "main",
        )}`,
      );
    case "hov-1-gain-tries-too":
      return conditions["hov-1-gain-tries-too"](
        `x${formatForContext(
          huge(`1e${HOV_1_GAIN_TRIES_TOO_LOG10_THRESHOLD}`),
          "main",
        )}`,
      );
    case "hov-1-gain-gets-serious":
      return conditions["hov-1-gain-gets-serious"](
        `x${formatForContext(
          huge(`1e${HOV_1_GAIN_GETS_SERIOUS_LOG10_THRESHOLD}`),
          "main",
        )}`,
      );
    case "hov-9-gain-upgrade-milestone":
      return conditions["hov-9-gain-upgrade-milestone"](
        H9G_UPGRADE_MILESTONE_ACHIEVEMENT_LEVEL,
      );
    case "one-cost-batch-upgrade":
      return conditions["one-cost-batch-upgrade"](
        ONE_COST_BATCH_UPGRADE_HOV_GAIN_LEVEL,
      );
    case "burn-the-core":
      return conditions["burn-the-core"](
        BURN_THE_CORE_TOTAL_HOV_CORE_SPENT_THRESHOLD,
      );
    case "amplifier-upgrade":
      return conditions["amplifier-upgrade"](
        AMPLIFIER_UPGRADE_PURCHASE_THRESHOLD,
      );
    case "exploration-tim-acceleration":
      return conditions["exploration-tim-acceleration"](
        EXPLORATION_TIM_ACCELERATION_UNLOCK_CUMULATIVE_MS / 1_000,
      );
    case "exploration-biome-level-two":
      return conditions["exploration-biome-level-two"](
        EXPLORATION_BIOME_LEVEL_ACHIEVEMENT_THRESHOLD,
      );
    default:
      return conditions[id];
  }
}

function resolveSecretCondition(
  id: SecretAchievementId,
  conditions: AppMessages["achievements"]["secrets"]["conditions"],
): string {
  switch (id) {
    case "heavy-pointer":
      return conditions["heavy-pointer"](HEAVY_POINTER_HOLD_MINUTES);
    case "hov-o-label":
      return conditions["hov-o-label"](HOV_O_LABEL_HOVER_SECONDS);
    case "target-rapid-click":
      return conditions["target-rapid-click"](
        HOV_CELL_RAPID_CLICK_TARGET,
        HOV_CELL_RAPID_CLICK_WINDOW_SECONDS,
      );
    default:
      return conditions[id];
  }
}

function resolveSecretEffect(
  id: SecretAchievementId,
  effects: AppMessages["achievements"]["secrets"]["effects"],
): string {
  switch (id) {
    case "x-pattern-hover":
    case "hov-o-label":
    case "h-pattern-unlock":
    case "h-pattern-hover":
    case "target-rapid-click":
      return effects[id](ACHIEVEMENT_SECRET_TOKEN_GRANT);
    default:
      return effects[id];
  }
}

function resolveNormalEffect(
  id: NormalAchievementId,
  effects: AppMessages["achievements"]["effects"],
): string {
  switch (id) {
    case "second-merge-hov":
      return effects["second-merge-hov"](SECOND_MHOV_HG_MULTIPLIER);
    case "multi-branch-new-hov":
      return effects["multi-branch-new-hov"](NEW_NEW_HOV_HG_MULTIPLIER);
    case "fourth-hov-gain":
      return effects["fourth-hov-gain"](FOURTH_HOV_GAIN_MP_BONUS);
    case "ninth-hov-gain":
      return effects["ninth-hov-gain"](NINTH_HOV_GAIN_MP_BONUS);
    case "mass-hov-gain-family-upgrade":
      return effects["mass-hov-gain-family-upgrade"](
        MASS_HOV_GAIN_FAMILY_UPGRADE_MP_BONUS,
      );
    case "music-pointer":
      return effects["music-pointer"](MUSIC_POINTER_MP_BONUS);
    case "movie-pointer":
      return effects["movie-pointer"](MOVIE_POINTER_MP_BONUS);
    case "maschinen-pointer":
      return effects["maschinen-pointer"](MASCHINEN_POINTER_MP_BONUS);
    case "mhov-mp-execution-ten":
      return effects["mhov-mp-execution-ten"](MHOV_MP_EXECUTION_TEN_MP_BONUS);
    case "time-has-come":
      return effects["time-has-come"](
        TIM_HOV_ACTIVATION_EFFICIENCY_AFTER_TIME_HAS_COME,
      );
    case "google":
      return effects["google"](GOOGLE_HG_POWER_EXPONENT_BONUS);
    case "global-hov-gain-upgrade-milestone":
      return effects["global-hov-gain-upgrade-milestone"](
        UPGRADE_MILESTONE_INTERVAL,
      );
    case "global-hov-gain-upgrade-tenth-milestone":
      return effects["global-hov-gain-upgrade-tenth-milestone"](
        GLOBAL_HOV_GAIN_UPGRADE_TENTH_MILESTONE_HG_POWER_EXPONENT_BONUS,
      );
    case "massive-merge-hov":
      return effects["massive-merge-hov"](
        MASSIVE_MERGE_HOV_HG_POWER_EXPONENT_BONUS,
      );
    case "rank-gap":
      return effects["rank-gap"](RANK_GAP_HG_POWER_EXPONENT_BONUS);
    case "tenth-outside-hov":
      return effects["tenth-outside-hov"](
        TENTH_OUTSIDE_HOV_HG_POWER_EXPONENT_BONUS,
      );
    case "hov-1-gain-tries-too":
      return effects["hov-1-gain-tries-too"](
        HOV_1_GAIN_TRIES_TOO_H1G_EXPONENT_BONUS,
      );
    case "hov-1-gain-gets-serious":
      return effects["hov-1-gain-gets-serious"](
        HOV_1_GAIN_GETS_SERIOUS_H1G_EXPONENT_BONUS,
      );
    case "not-fully-free":
      return effects["not-fully-free"](NOT_FULLY_FREE_HG_HNG_COST_DIVISOR);
    case "cheap-fourth-hov-gain":
      return effects["cheap-fourth-hov-gain"](
        CHEAP_FOURTH_HOV_GAIN_HG_HNG_COST_DIVISOR,
      );
    case "cheap-global-hov-gain-milestone":
      return effects["cheap-global-hov-gain-milestone"](
        CHEAP_GLOBAL_HOV_GAIN_MILESTONE_HOV_GAIN_FAMILY_COST_DIVISOR,
      );
    case "burn-the-core":
      return effects["burn-the-core"](
        formatHovSkillCoordinate(
          getHovSkillDefinition("ohov-hov-core-gain-multiplier-bonus").row,
          getHovSkillDefinition("ohov-hov-core-gain-multiplier-bonus").column,
        ),
      );
    case "exploration-biome-level-two":
      return effects["exploration-biome-level-two"](
        EXPLORATION_BIOME_LEVEL_MP_BONUS,
      );
    default:
      return effects[id];
  }
}

interface AchievementDisplay {
  readonly name: string;
  readonly shortLabel: string;
  readonly condition: string;
  readonly effect: string;
  readonly hint: string;
}

function resolveAchievementDisplay(
  definition: AchievementDefinition,
  unlocked: boolean,
  messages: AppMessages,
): AchievementDisplay {
  if (definition.category === "secret") {
    const secrets = messages.achievements.secrets;
    return {
      name: unlocked ? secrets.names[definition.id] : secrets.maskedName,
      shortLabel: unlocked
        ? secrets.shortLabels[definition.id]
        : secrets.maskedShortLabel,
      condition: unlocked
        ? resolveSecretCondition(definition.id, secrets.conditions)
        : "",
      effect: unlocked
        ? resolveSecretEffect(definition.id, secrets.effects)
        : "",
      hint: secrets.hints[definition.id],
    };
  }
  const normal = messages.achievements;
  return {
    name: normal.names[definition.id],
    shortLabel: normal.shortLabels[definition.id],
    condition: resolveNormalCondition(definition.id, normal.conditions),
    effect: resolveNormalEffect(definition.id, normal.effects),
    hint: "",
  };
}

interface AchievementsViewOptions {
  readonly container: HTMLElement;
  readonly onAchievementSeen: (id: AchievementId) => void;
  readonly onTabOpened: (category: AchievementCategory) => void;
}

interface AchievementsViewUpdateOptions {
  readonly showUnseenDots?: boolean;
}

type AchievementTabId = AchievementCategory;

interface IconNodes {
  readonly button: HTMLButtonElement;
  readonly displayIndex: HTMLSpanElement;
  readonly label: HTMLSpanElement;
  readonly tooltip: HTMLDivElement;
  readonly tooltipName: HTMLDivElement;
  readonly tooltipHint: HTMLDivElement;
  readonly tooltipCondition: HTMLDivElement;
  readonly tooltipEffect: HTMLDivElement;
}

export class AchievementsView implements Disposable {
  private readonly root: HTMLDivElement;
  private readonly tabs: ReadonlyMap<AchievementTabId, HTMLButtonElement>;
  private readonly panels: ReadonlyMap<AchievementTabId, HTMLDivElement>;
  private readonly summaries: ReadonlyMap<AchievementTabId, HTMLDivElement>;
  private readonly normalGrid: HTMLDivElement;
  private readonly secretGrid: HTMLDivElement;
  private readonly normalSummary: HTMLDivElement;
  private readonly secretSummary: HTMLDivElement;
  private readonly iconNodes = new Map<AchievementId, IconNodes>();
  private readonly cleanups: Array<() => void> = [];
  private readonly onAchievementSeen: (id: AchievementId) => void;
  private readonly onTabOpened: (category: AchievementCategory) => void;
  private readonly tooltipController: StickyTooltipController =
    createStickyTooltipController();
  private readonly recentlyUnlocked = new Set<AchievementId>();
  private activeTab: AchievementTabId = "normal";

  private readonly handleRootPointerDown = (event: PointerEvent): void => {
    this.tooltipController.closeIfOutsidePointerTarget(event.target);
  };

  constructor(options: AchievementsViewOptions) {
    const messages = getMessages();
    this.onAchievementSeen = options.onAchievementSeen;
    this.onTabOpened = options.onTabOpened;

    this.root = document.createElement("div");
    this.root.className = "hover-idle-achievements";

    const tabBar = document.createElement("div");
    tabBar.className = "hover-idle-core-tabs";
    tabBar.setAttribute("role", "tablist");
    tabBar.setAttribute("aria-label", messages.achievements.tabsAria);

    const tabDefs: ReadonlyArray<{
      readonly id: AchievementTabId;
      readonly label: string;
      readonly draft: boolean;
    }> = [
      { id: "normal", label: messages.achievements.tabs.normal, draft: false },
      { id: "secret", label: messages.achievements.tabs.secret, draft: false },
    ];

    const tabs = new Map<AchievementTabId, HTMLButtonElement>();
    const panels = new Map<AchievementTabId, HTMLDivElement>();
    const summaries = new Map<AchievementTabId, HTMLDivElement>();

    for (const def of tabDefs) {
      const tabButton = document.createElement("button");
      tabButton.type = "button";
      tabButton.className = "hover-idle-core-tab hover-idle-achievements-tab";
      if (def.draft)
        tabButton.classList.add("hover-idle-achievements-tab-draft");
      tabButton.textContent = def.label;
      tabButton.dataset.tab = def.id;
      tabButton.dataset.active = def.id === this.activeTab ? "true" : "false";
      tabButton.setAttribute("role", "tab");
      tabButton.setAttribute(
        "aria-selected",
        def.id === this.activeTab ? "true" : "false",
      );

      const handler = (): void => this.switchTab(def.id);
      this.addTabIntentListeners(tabButton, handler);
      this.cleanups.push(() => {
        this.removeTabIntentListeners(tabButton, handler);
      });

      tabBar.append(tabButton);
      tabs.set(def.id, tabButton);
    }

    this.tabs = tabs;

    const normalPanel = document.createElement("div");
    normalPanel.className = "hover-idle-achievements-panel";
    normalPanel.dataset.tab = "normal";
    normalPanel.setAttribute("role", "tabpanel");

    this.normalGrid = document.createElement("div");
    this.normalGrid.className = "hover-idle-achievements-grid";
    normalPanel.append(this.normalGrid);
    panels.set("normal", normalPanel);

    const secretPanel = document.createElement("div");
    secretPanel.className = "hover-idle-achievements-panel";
    secretPanel.dataset.tab = "secret";
    secretPanel.setAttribute("role", "tabpanel");
    secretPanel.hidden = true;
    this.secretGrid = document.createElement("div");
    this.secretGrid.className = "hover-idle-achievements-grid";
    secretPanel.append(this.secretGrid);
    panels.set("secret", secretPanel);

    this.panels = panels;

    this.normalSummary = document.createElement("div");
    this.normalSummary.className = "hover-idle-achievements-summary";
    this.normalSummary.dataset.tab = "normal";
    summaries.set("normal", this.normalSummary);

    this.secretSummary = document.createElement("div");
    this.secretSummary.className = "hover-idle-achievements-summary";
    this.secretSummary.dataset.tab = "secret";
    this.secretSummary.hidden = true;
    summaries.set("secret", this.secretSummary);

    this.summaries = summaries;

    this.root.append(
      tabBar,
      this.normalSummary,
      this.secretSummary,
      normalPanel,
      secretPanel,
    );
    options.container.replaceChildren(this.root);

    this.root.addEventListener("pointerdown", this.handleRootPointerDown);
    this.cleanups.push(() => {
      this.root.removeEventListener("pointerdown", this.handleRootPointerDown);
    });
  }

  notifyShown(): void {
    this.onTabOpened(this.activeTab);
  }

  notifyHidden(): void {
    this.clearRecentlyUnlocked();
  }

  update(
    snapshot: AchievementsSnapshot,
    options: AchievementsViewUpdateOptions = {},
  ): void {
    const messages = getMessages();
    const showUnseenDots = options.showUnseenDots ?? true;
    this.tabs.get("normal")!.dataset.unseen =
      showUnseenDots && snapshot.normalUnseenCount > 0 ? "true" : "false";
    this.tabs.get("secret")!.dataset.unseen =
      showUnseenDots && snapshot.secretUnseenCount > 0 ? "true" : "false";
    this.normalSummary.textContent =
      messages.achievements.hovGainCountMultiplierLabel(
        snapshot.hovGainCountMultiplier,
      );
    this.secretSummary.textContent =
      messages.achievements.timGainCountMultiplierLabel(
        snapshot.timGainCountMultiplier,
      );
    for (const entry of snapshot.entries) {
      const { definition, displayIndex, unlocked, seen } = entry;
      const nodes = this.ensureIcon(definition.id, definition.category);
      nodes.button.dataset.unlocked = unlocked ? "true" : "false";
      nodes.button.dataset.unseen =
        showUnseenDots && unlocked && !seen ? "true" : "false";
      this.syncRecentUnlockClass(nodes.button, definition.id, unlocked);

      const isSecret = definition.category === "secret";
      const revealed = !isSecret || unlocked;
      const display = resolveAchievementDisplay(definition, unlocked, messages);

      nodes.displayIndex.textContent = String(displayIndex);
      nodes.label.textContent = display.shortLabel;
      nodes.tooltipName.textContent = display.name;
      nodes.tooltipHint.textContent = display.hint;
      nodes.tooltipHint.hidden = !isSecret || display.hint === "";
      nodes.tooltipCondition.textContent = display.condition;
      nodes.tooltipCondition.hidden = !revealed;
      const hasEffect = display.effect !== "";
      nodes.tooltipEffect.textContent = hasEffect
        ? messages.achievements.effectLabel + display.effect
        : "";
      nodes.tooltipEffect.dataset.unlocked = unlocked ? "true" : "false";
      nodes.tooltipEffect.hidden = !revealed || !hasEffect;
    }
  }

  dispose(): void {
    this.tooltipController.dispose();
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups.length = 0;
    this.root.remove();
  }

  markRecentlyUnlocked(ids: readonly AchievementId[]): void {
    for (const id of ids) {
      this.recentlyUnlocked.add(id);
      const nodes = this.iconNodes.get(id);
      if (nodes !== undefined) {
        nodes.button.classList.add(RECENT_UNLOCK_CLASS);
      }
    }
  }

  private addTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.addEventListener("pointerenter", handler);
    button.addEventListener("focus", handler);
    button.addEventListener("click", handler);
  }

  private removeTabIntentListeners(
    button: HTMLButtonElement,
    handler: () => void,
  ): void {
    button.removeEventListener("pointerenter", handler);
    button.removeEventListener("focus", handler);
    button.removeEventListener("click", handler);
  }

  private switchTab(id: AchievementTabId): void {
    if (id === this.activeTab) return;

    const previousTab = this.activeTab;
    for (const achievementId of [...this.recentlyUnlocked]) {
      const button = this.iconNodes.get(achievementId)?.button;
      if (button?.dataset.category === previousTab) {
        this.clearRecentUnlock(achievementId);
      }
    }

    this.tooltipController.close();
    this.panels.get(previousTab)!.hidden = true;
    this.panels.get(id)!.hidden = false;
    for (const [tabId, button] of this.tabs) {
      const isActive = tabId === id;
      button.dataset.active = isActive ? "true" : "false";
      button.setAttribute("aria-selected", isActive ? "true" : "false");
    }
    this.summaries.get(previousTab)!.hidden = true;
    this.summaries.get(id)!.hidden = false;
    this.activeTab = id;
    this.onTabOpened(id);
  }

  private ensureIcon(
    id: AchievementId,
    category: AchievementCategory,
  ): IconNodes {
    const existing = this.iconNodes.get(id);
    if (existing) return existing;

    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-achievement-icon";
    button.dataset.achievement = id;
    button.dataset.category = category;

    const displayIndex = document.createElement("span");
    displayIndex.className = "hover-idle-achievement-display-index";

    const label = document.createElement("span");
    label.className = "hover-idle-achievement-icon-label";

    const tooltip = document.createElement("div");
    tooltip.className = "hover-idle-achievement-tooltip";
    tooltip.dataset.placement = "bottom";
    tooltip.setAttribute("role", "tooltip");
    tooltip.setAttribute("aria-hidden", "true");
    tooltip.hidden = true;

    const tooltipName = document.createElement("div");
    tooltipName.className = "hover-idle-achievement-tooltip-name";

    const tooltipHint = document.createElement("div");
    tooltipHint.className = "hover-idle-achievement-tooltip-hint";

    const tooltipCondition = document.createElement("div");
    tooltipCondition.className = "hover-idle-achievement-tooltip-condition";

    const tooltipEffect = document.createElement("div");
    tooltipEffect.className = "hover-idle-achievement-tooltip-effect";

    tooltip.append(tooltipName, tooltipHint, tooltipCondition, tooltipEffect);

    button.append(displayIndex, label, tooltip);

    const seenHandler = (): void => {
      this.clearRecentUnlock(id);
      this.onAchievementSeen(id);
    };
    const handlePointerEnter = (event: PointerEvent): void => {
      if (event.pointerType === "touch") return;
      this.openTooltip(id, button, tooltip, "hover");
    };
    const handlePointerLeave = (event: PointerEvent): void => {
      if (event.pointerType === "touch") return;
      this.removeTooltipSource(id, "hover");
    };
    const handlePointerDown = (event: PointerEvent): void => {
      if (event.pointerType !== "touch") return;
      this.openTooltip(id, button, tooltip, "sticky");
    };
    const handleFocus = (): void => {
      this.openTooltip(id, button, tooltip, "focus");
    };
    const handleBlur = (): void => {
      this.removeTooltipSource(id, "focus");
    };
    button.addEventListener("pointerenter", seenHandler);
    button.addEventListener("focus", seenHandler);
    button.addEventListener("pointerdown", seenHandler);
    button.addEventListener("pointerenter", handlePointerEnter);
    button.addEventListener("pointerleave", handlePointerLeave);
    button.addEventListener("pointerdown", handlePointerDown);
    button.addEventListener("focus", handleFocus);
    button.addEventListener("blur", handleBlur);
    this.cleanups.push(() => {
      button.removeEventListener("pointerenter", seenHandler);
      button.removeEventListener("focus", seenHandler);
      button.removeEventListener("pointerdown", seenHandler);
      button.removeEventListener("pointerenter", handlePointerEnter);
      button.removeEventListener("pointerleave", handlePointerLeave);
      button.removeEventListener("pointerdown", handlePointerDown);
      button.removeEventListener("focus", handleFocus);
      button.removeEventListener("blur", handleBlur);
    });

    const targetGrid =
      category === "secret" ? this.secretGrid : this.normalGrid;
    targetGrid.append(button);

    const nodes: IconNodes = {
      button,
      displayIndex,
      label,
      tooltip,
      tooltipName,
      tooltipHint,
      tooltipCondition,
      tooltipEffect,
    };
    this.iconNodes.set(id, nodes);
    return nodes;
  }

  private openTooltip(
    id: AchievementId,
    button: HTMLButtonElement,
    tooltip: HTMLDivElement,
    source: StickyTooltipSource,
  ): void {
    this.tooltipController.open({
      id,
      reference: button,
      tooltip,
      source,
      floatingOptions: ACHIEVEMENT_FLOATING_TOOLTIP_OPTIONS,
    });
  }

  private removeTooltipSource(
    id: AchievementId,
    source: StickyTooltipSource,
  ): void {
    this.tooltipController.removeSource(id, source);
  }

  private syncRecentUnlockClass(
    button: HTMLButtonElement,
    id: AchievementId,
    unlocked: boolean,
  ): void {
    if (this.recentlyUnlocked.has(id) && unlocked) {
      button.classList.add(RECENT_UNLOCK_CLASS);
    } else {
      button.classList.remove(RECENT_UNLOCK_CLASS);
    }
  }

  private clearRecentUnlock(id: AchievementId): void {
    if (!this.recentlyUnlocked.delete(id)) return;
    this.iconNodes.get(id)?.button.classList.remove(RECENT_UNLOCK_CLASS);
  }

  private clearRecentlyUnlocked(): void {
    if (this.recentlyUnlocked.size === 0) return;
    const ids = [...this.recentlyUnlocked];
    this.recentlyUnlocked.clear();
    for (const id of ids) {
      this.iconNodes.get(id)?.button.classList.remove(RECENT_UNLOCK_CLASS);
    }
  }
}
